import {
  JobHistoryModel,
  HistoryGroupModel,
  SequenceRowModel,
  CoordItemModel,
  CdrLengthRangeModel,
  SequenceModel,
  SequenceRowMapModel,
  ConstraintModel,
} from "@services/home/model/home.model";
import { NEWLINE_REGEX } from "@services/common/config/common.config";
import { NameValueModel } from "@services/common/model/gloabal.model";
import { replaceWhitespace } from "@utils/common.util";
import { BinderDesignTypeEnum } from "@services/home/enum/home.enum";
import { AlphaBet } from "@services/home/config/home.config";
import { RDKitModule } from "@rdkit/rdkit";
import { parseRanges } from "./validator";

export function groupJobHistory(list: JobHistoryModel[]) {
  const now = Date.now();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const oneDay = 24 * 60 * 60 * 1000;
  const sevenDaysAgo = now - 7 * oneDay;
  const thirtyDaysAgo = now - 30 * oneDay;
  const currentYear = new Date().getFullYear();

  const today: JobHistoryModel[] = [];
  const last7days: JobHistoryModel[] = [];
  const last30days: JobHistoryModel[] = [];
  const thisYear: Record<string, JobHistoryModel[]> = {};

  const older: Record<string, JobHistoryModel[]> = {};

  list.forEach((item) => {
    const itemDate = new Date(item.created_at_ts13);
    const year = itemDate.getFullYear();
    const month = `${itemDate.getMonth() + 1}`.padStart(2, "0"); // 确保月份有两位数

    // 判断"今天"
    if (item.created_at_ts13 >= todayStart.getTime()) {
      today.push(item);

      // 判断 "最近7天"
    } else if (item.created_at_ts13 >= sevenDaysAgo) {
      last7days.push(item);

      // 判断 "最近30天"
    } else if (item.created_at_ts13 >= thirtyDaysAgo) {
      last30days.push(item);

      // 今年内按月份分组
    } else if (year === currentYear) {
      const monthKey = `${year}-${month}`;
      if (!thisYear[monthKey]) thisYear[monthKey] = [];
      thisYear[monthKey].push(item);

      // 更早按年份分组
    } else {
      if (!older[year]) older[year] = [];
      older[year].push(item);
    }
  });

  const groups: HistoryGroupModel[] = [];

  if (today.length > 0) {
    groups.push({
      group_name: "Today",
      list: today,
    });
  }

  if (last7days.length > 0) {
    groups.push({
      group_name: "Within 7 days",
      list: last7days,
    });
  }
  if (last30days.length > 0) {
    groups.push({
      group_name: "Within 30 days",
      list: last30days,
    });
  }

  const thisYearKeys = Object.keys(thisYear);
  if (thisYearKeys.length) {
    const sortedKeys = thisYearKeys.sort((a, b) => {
      const aMonth = +a.split("-")[1];

      const bMonth = +b.split("-")[1];

      return bMonth - aMonth;
    });

    sortedKeys.forEach((k) => {
      groups.push({
        group_name: k,
        list: thisYear[k],
      });
    });
  }

  const olderKeys = Object.keys(older);
  if (olderKeys.length > 0) {
    const sortedKeys = olderKeys.sort((a, b) => {
      const aYear = +a;
      const bYear = +b;

      return bYear - aYear;
    });

    sortedKeys.forEach((k) => {
      groups.push({
        group_name: k,
        list: older[k],
      });
    });
  }

  return groups;
}

export function checkRna(val: string) {
  const reg = "AUCG";

  const text = replaceWhitespace(val);
  if (!text) return "This field is required";
  const len = text.length;
  for (let i = 0; i < len; i++) {
    const c = text[i];

    if (!reg.includes(c)) {
      return `Found unsupported RNA '${c}'`;
    }
  }

  return "";
}

export function checkDna(val: string) {
  const reg = "ATCG";

  const text = replaceWhitespace(val);
  if (!text) return "This field is required";
  const len = text.length;
  for (let i = 0; i < len; i++) {
    const c = text[i];

    if (!reg.includes(c)) {
      return `Found unsupported DNA '${c}'`;
    }
  }

  return "";
}

export async function checkLigandSmiles(val: string) {
  const text = replaceWhitespace(val);

  if (!text) return "This field is required";

  try {
    const RDKit = await window.initRDKitModule();

    const molecule = RDKit.get_mol(text);

    if (!molecule) {
      return "Invalid smiles";
    }

    return "";
  } catch (e) {
    console.log(e);

    return "Invalid smiles";
  }
}

export function checkPdbId(val: string) {
  const text = replaceWhitespace(val);

  const reg = /[0-9][A-Za-z0-9]{3}/;

  if (!text) return "This field is required";

  if (!reg.test(text)) {
    return `Invalid PDB ID ${val}`;
  }
}

export function checkAlphafoldId(val: string) {
  const text = replaceWhitespace(val);

  const reg =
    /[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}/;

  if (!text) return "This field is required";

  if (!reg.test(text)) {
    return `Invalid UniProt ID ${val}`;
  }
}

export function getChainIds(
  sequences: SequenceRowModel[],
  i: number,
  count: number
) {
  if (i === 0) {
    const arr: string[] = [];
    for (let j = 0; j < count; j++) {
      const residue = AlphaBet[j];
      arr.push(residue);
    }

    return arr.join(",");
  } else {
    const arr: string[] = [];
    let start = 0;
    for (let j = 0; j < i; j++) {
      start += sequences[j].copies;
    }

    for (let k = start; k < start + count; k++) {
      const residue = AlphaBet[k];
      arr.push(residue);
    }

    return arr.join(",");
  }
}

export function getProvideResidueMap(list?: CoordItemModel[]) {
  if (!list) {
    return {};
  }

  const segmentRanges = list.map((item) => {
    const arr = parseRanges(item.seq_segment);
    const nums: number[] = [];

    const [start, end] = arr[0];

    for (let i = start; i <= end; i++) {
      nums.push(i);
    }
    return nums;
  });

  const map = segmentRanges.reduce((ac, arr) => {
    return arr.reduce((a, n) => {
      a[n] = true;
      return a;
    }, ac);
  }, {} as Record<number, boolean>);

  return map;
}

export function getHotspotResidueMap(hotspots?: string) {
  if (!hotspots) {
    return {};
  }

  const list = hotspots.split(",");
  const segmentRanges = list.map((hotspot) => {
    const arr = parseRanges(hotspot);
    const nums: number[] = [];

    const [start, end] = arr[0];

    for (let i = start; i <= end; i++) {
      nums.push(i);
    }
    return nums;
  });

  const map = segmentRanges.reduce((ac, arr) => {
    return arr.reduce((a, n) => {
      a[n] = true;
      return a;
    }, ac);
  }, {} as Record<number, boolean>);

  return map;
}

export function getDockingHotspotResidueMap(
  constraints?: ConstraintModel[] | null,
  chainName?: string
) {
  const targetConstraint = (constraints || []).find((constraint) => {
    return constraint?.chain_id_b === chainName;
  });

  const hotspots = targetConstraint?.resi_ids_b;

  return getHotspotResidueMap(hotspots);
}

export function getBinderPlaceholder(type?: string) {
  if (type === BinderDesignTypeEnum.Ligand) {
    return `Enter a valid SMILES string, e.g. CCCC(C=O)O`;
  } else if (type === BinderDesignTypeEnum.LigandCCD) {
    return `Enter a valid CCD code, e.g. URC`;
  } else if (type === BinderDesignTypeEnum.RnaSequence) {
    return `Enter an RNA sequence, e.g. AUCGGCAAAGGAGCCCAA...`;
  } else if (type === BinderDesignTypeEnum.RnaSequenceDs) {
    return `Enter an RNA sequence, e.g. AUCGGCAAAGGAGCCCAA... The complementary sequence will be derived automatically.`;
  } else if (type === BinderDesignTypeEnum.DnaSequence) {
    return `Enter an DNA sequence, e.g. AUCGGCAAAGGAGCCCAA...`;
  } else if (type === BinderDesignTypeEnum.DnaSequenceDs) {
    return `Enter a DNA sequence, e.g. GCCAATTA... The reverse complement will be derived automatically.`;
  } else if (type === BinderDesignTypeEnum.ProteinChain) {
    return `Enter a protein sequence, e.g. GQLEEIAKQLEEIAWQLEEIAQG...`;
  }

  return "";
}

export function processDockingFasta(content: string) {
  const trimedContent = content.replace(NEWLINE_REGEX, "\n");
  const res = trimedContent.split(">").filter((item) => !!item);
  const list: NameValueModel[] = res.map((s) => {
    const [name, ...rest] = s.split("\n");
    return {
      name,
      value: rest.join("\n"),
    };
  });

  if (list.length > 6) {
    return {
      errorMsg: "Too many (>6) chains in a protein",
    };
  }

  return list;
}

export function formatCdrLengthRange(ranges: CdrLengthRangeModel[]) {
  const list = ranges.map((range) => {
    if (range.min === range.max) {
      return `${range.min}`;
    } else {
      return `${range.min}-${range.max}`;
    }
  });

  return list.join(" | ");
}

export function formatStructureTemplates(sequences: SequenceModel[]) {
  const list = sequences
    .filter((sequence) => {
      return sequence.pdb_input_value;
    })
    .map((sequence) => {
      return `${sequence.pdb_input_value!}: ${sequence.chain_ids.join(",")}`;
    });
  return list.join("; ");
}

export function parseCCDSequence(text?: string) {
  if (!text) {
    return [];
  }

  const sequence = replaceWhitespace(text);

  const result: string[] = [];
  const regex = /\([^)]+\)|[^()]+/g;

  let match: RegExpExecArray | null = null;
  while ((match = regex.exec(sequence)) !== null) {
    const part = match[0];
    if (part.startsWith("(") && part.endsWith(")")) {
      // 括号部分整体 push
      result.push(part);
    } else {
      // 非括号部分拆分为单字符
      result.push(...part.split(""));
    }
  }

  return result;
}

export function scrollToLine(
  textarea: HTMLTextAreaElement,
  lineNumber: number
) {
  const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);

  // If lineHeight cannot be parsed (e.g., "normal"), estimate it
  const estimated = lineHeight || 20;

  // Compute scrollTop position
  const scrollY = lineNumber * estimated;

  textarea.scrollTop = scrollY;
}

export function getEditRowIndex(
  spans: NodeListOf<HTMLSpanElement>,
  targetSpan: HTMLSpanElement
) {
  const lines: SequenceRowMapModel[] = [];
  for (const span of spans) {
    const top = span.offsetTop;
    let line = lines.find((l) => l.top === top);
    if (!line) {
      line = { top, items: [] };
      lines.push(line);
    }
    line.items.push(span);
  }

  const lineIndex = lines.findIndex((l) => l.items.includes(targetSpan));

  return lineIndex;
}
